// Shared helpers for the "Global Sort Order" parcha rendering fix.
//
// The admin can now define one flat sort sequence per shop+type that freely mixes
// categories (e.g. Whisky #1, Beer #2, Whisky #3). Every parcha screen needs to:
//   1. Sort its fetched rows by that authoritative sequence (applySortOrder), and
//   2. Print a category header only when the category actually changes from the
//      previous row in that sequence, instead of gathering every row of a category
//      together regardless of position (buildCategorySegments).
import apiClient from "../apiClient";

// Fetches the authoritative {productId: sortOrder} map for a shop+type from the new
// read-only /Liquor/GetProductSortOrder endpoint. Never throws — screens should still
// render (falling back to whatever order the stock endpoint returned) if this fails.
export async function fetchSortOrderMap(shopId, typeId) {
  try {
    const res = await apiClient.post("/Liquor/GetProductSortOrder", { shopId, typeId });
    const pairs = res.data?.data || [];
    const map = {};
    pairs.forEach((p) => { map[p.productId ?? p.ProductId] = p.sortOrder ?? p.SortOrder; });
    return map;
  } catch {
    return {};
  }
}

// Stable-sorts `rows` by `sortMap[row.id]`. Rows missing from the map (no sort order
// set yet for that shop) fall back to their existing array position, sinking after
// every row that does have an assigned order.
export function applySortOrder(rows, sortMap) {
  if (!sortMap || Object.keys(sortMap).length === 0) return rows;
  return rows
    .map((row, index) => ({ row, index, order: sortMap[row.id] }))
    .sort((a, b) => {
      const aHas = a.order !== undefined;
      const bHas = b.order !== undefined;
      if (aHas && bHas) return a.order - b.order || a.index - b.index;
      if (aHas) return -1;
      if (bHas) return 1;
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}

// Walks `rows` sequentially and returns [{ cat, rows: [...] }], starting a new segment
// only when `cat` differs from the previous row's `cat` — this is what makes a category
// header print exactly where the sequence's category changes, instead of gathering all
// rows of that category together.
export function buildCategorySegments(rows, getCat = (r) => r.cat) {
  const segments = [];
  rows.forEach((row) => {
    const cat = getCat(row);
    const last = segments[segments.length - 1];
    if (last && last.cat === cat) {
      last.rows.push(row);
    } else {
      segments.push({ cat, rows: [row] });
    }
  });
  return segments;
}
